import { USER_ACTIONS as type } from "@common/constants/actionTypes";
import * as userService from "@profile/services/userService";
import { generateErrorString } from "@common/services/errorService";
import analyticsService, {
  ANALYTICS_TYPES,
} from "@common/services/analyticsService";
import { getSharedFavorites } from "@beers/services/recipeService";

/* Helper to sync user profile data to OneSignal contact channels */
const syncOneSignalUserDetails = (data, errorContext = "sync") => {
  if (window.OneSignalDeferred) {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        if (
          OneSignal &&
          typeof OneSignal.login === "function" &&
          OneSignal.User
        ) {
          await OneSignal.login(data.uuid);
          if (data.email && typeof OneSignal.User.addEmail === "function") {
            OneSignal.User.addEmail(data.email);
          }
          if (data.phone && typeof OneSignal.User.addSms === "function") {
            OneSignal.User.addSms(data.phone);
          } else if (
            data.phone_number &&
            typeof OneSignal.User.addSms === "function"
          ) {
            OneSignal.User.addSms(data.phone_number);
          }
        }
      } catch (err) {
        console.warn(
          `OneSignal user sync skipped (${errorContext}):`,
          err?.message || err,
        );
      }
    });
  }
};

/* set notification permissions */
const setPermission = (permission) => ({
  type: type.SET_PERMISSION,
  permission,
});

export const setNotificationPermission = (permission) => (dispatch) => {
  dispatch(setPermission(permission));
};
/* GET */
const getUser = () => ({ type: type.GET_USER });

const getUserSuccess = (userInformation) => ({
  type: type.GET_USER_SUCCESS,
  userInformation,
});

const getSharedFavouritesSuccess = (favourites) => ({
  type: type.GET_SHARED_FAVOURITES_SUCCESS,
  favourites,
});

const getSubscriptionSuccess = (accessType) => ({
  type: type.GET_SUBSCRIPTIONS_SUCCESS,
  // Preserved deliberately: Account.jsx destructures `subscription` from
  // userInfo for its membership panel + cancel flow. The old `data[0]`
  // handling evaluated to null on every call anyway (the endpoint returns
  // an object, not an array), so keep that exact outcome rather than
  // populating it with a response object that has no subscription_id or
  // end_date -- doing that would wake a currently-dead panel and render a
  // broken cancel button.
  subscription: null,
  accessType,
});

/* The endpoint returns an OBJECT, not an array -- the old `data[0]` was
 * Mollie-list-era legacy and evaluated to undefined on every call, so the
 * answer was thrown away. access_type is an int and STARTER is 0, so this
 * must test for a number rather than truthiness. */
const accessTypeFrom = (data) =>
  data && typeof data.access_type === "number" ? data.access_type : null;

const cancelSubscriptonStart = () => ({ type: type.CANCEL_SUBSCRIPTION });
const cancelSubscriptionSuccess = (response) => ({
  type: type.CANCEL_SUBSCRIPTION_SUCCESS,
  response,
});
const cancelSubscriptionError = (errorMessage) => ({
  type: type.CANCEL_SUBSCRIPTION_ERROR,
  errorMessage,
});
const getUserError = (message) => ({ type: type.GET_USER_ERROR, message });

export const getUserInformation = () => (dispatch) => {
  dispatch(getUser());
  userService
    .getCurrentUser()
    .then((data) => {
      if (!localStorage.getItem("userId")) {
        analyticsService.mixpanel.identify(data.uuid);
        analyticsService.mixpanel.people.set({
          UserID: data.uuid,
          $name: `${data.first_name} ${data.last_name}`,
          $first_name: data.first_name,
          $last_name: data.last_name,
          $email: data.email,
        });
        analyticsService.track(ANALYTICS_TYPES.SIGN_IN);
      }
      localStorage.setItem("userId", data.id);
      dispatch(getUserSuccess(data));
      syncOneSignalUserDetails(data, "sync");
    })
    .catch((response) => {
      if (response && typeof response.then === "function") {
        response.then(() => dispatch(getUserError(generateErrorString())));
      } else {
        dispatch(getUserError(generateErrorString()));
      }
    });
  userService
    .getCurrentSubscription()
    .then((data) => dispatch(getSubscriptionSuccess(accessTypeFrom(data))))
    .catch(() => {
      // Never let a subscription-status failure break the boot -- /me has
      // already supplied a tier. This promise previously had no catch at all.
      dispatch(getSubscriptionSuccess(null));
    });
};

export const getUsersSharedFavourites = () => (dispatch) => {
  getSharedFavorites().then((res) => {
    dispatch(getSharedFavouritesSuccess(res));
  });
};

export const cancelSubscription = (subscription) => (dispatch) => {
  dispatch(cancelSubscriptonStart());
  const payload = { subscription_id: subscription };
  userService
    .cancelSubscription(payload)
    .then((response) => {
      dispatch(cancelSubscriptionSuccess(response.subscription_id));
      userService
        .getCurrentSubscription()
        .then((data) => dispatch(getSubscriptionSuccess(accessTypeFrom(data))))
        .catch(() => dispatch(getSubscriptionSuccess(null)));
    })
    .catch((response) => {
      if (response && typeof response.then === "function") {
        response.then((res) => {
          dispatch(cancelSubscriptionError(res.message.toString()));
        });
      } else {
        dispatch(cancelSubscriptionError(generateErrorString()));
      }
    });
};
/* PATCH */
const patchUser = () => ({ type: type.PATCH_USER });

const patchUserSuccess = (userInformation) => ({
  type: type.PATCH_USER_SUCCESS,
  userInformation,
});

const patchUserError = (message) => ({ type: type.PATCH_USER_ERROR, message });

export const patchUserInformation = (data, callback) => (dispatch) => {
  dispatch(patchUser());
  userService
    .updateUserDetails(data)
    .then((data) => {
      localStorage.setItem("userId", data.id);
      dispatch(patchUserSuccess(data));
      syncOneSignalUserDetails(data, "update");
      callback && callback();
    })
    .catch((response) => {
      if (response && typeof response.then === "function") {
        response.then(() => dispatch(patchUserError(generateErrorString())));
      } else {
        dispatch(patchUserError(generateErrorString()));
      }
    });
};

export const removeUserData = () => (dispatch) => {
  dispatch({ type: type.REMOVE_USER_DATA });
};
